import { useState, useEffect, useCallback, useRef } from "react";
import { request } from "hooks/useOkoApi/useOkoApi";
import { normalizeGroupAssignments } from "hooks/useCurriculum/useCurriculum";
import { logEducatorDashboard, LogLevel } from "utils";

// Shared clear-to-reassign logic for the curriculum lesson-picker flow (lesson +
// standard bundles). When `isOpen`, loads every classroom-assigned code across all
// bundles (carrying each code's owning bundle_type + assignment_id). clearCodes()
// releases the selected codes immediately on confirm — routing by bundle type,
// since lesson and standard bundles live in different backend models:
//   • lesson bundles   → POST curriculum/.../group-assignments/unassign
//   • standard bundles → PUT  groups/.../assignments/{aid} (track_type:null)
export const useCurriculumClear = (classroomId, isOpen) => {
  const [clearableCodes, setClearableCodes] = useState([]);
  // Mirror the loaded list in a ref so clearCodes always reads the latest set even
  // if it was captured (via a handler closure) before the async load resolved.
  const clearableRef = useRef([]);

  useEffect(() => {
    if (!isOpen || !classroomId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const raw = await request(`curriculum/classrooms/${classroomId}/group-assignments`, { method: "GET" });
        if (cancelled) return;
        const flat = [];
        (normalizeGroupAssignments(raw).lessons || []).forEach((b) => {
          (b.assignments || []).forEach((a) => {
            if (a.group_code && a.track_type && a.track_type !== "no_assignment") {
              flat.push({
                group_code: a.group_code,
                standard_code: a.standard_code,
                standard_name: b.bundle_type === "standard"
                  ? (b.standard?.standard_name || a.standard_code || "")
                  : (b.lesson?.name || a.standard_code || ""),
                bundle_type: b.bundle_type,
                assignment_id: b.assignment_id,
              });
            }
          });
        });
        flat.sort((x, y) => x.group_code.localeCompare(y.group_code));
        clearableRef.current = flat;
        setClearableCodes(flat);
      } catch (e) {
        if (!cancelled) { clearableRef.current = []; setClearableCodes([]); }
        logEducatorDashboard("useCurriculumClear: failed to load clearable codes", { level: LogLevel.WARNING });
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, classroomId]);

  // Release the selected codes to the classroom pool, routed by owning bundle type.
  // Immediate, server-committed.
  const clearCodes = useCallback(async (selection) => {
    const items = clearableRef.current.filter((c) => selection.has(c.group_code));
    if (!items.length || !classroomId) return;

    const tasks = [];

    // Lesson-bundle codes: the curriculum batch-unassign resolves their owning bundle.
    const lessonCodes = items.filter((c) => c.bundle_type !== "standard").map((c) => c.group_code);
    if (lessonCodes.length) {
      tasks.push(request(`curriculum/classrooms/${classroomId}/group-assignments/unassign`, {
        method: "POST",
        payload: { group_codes: lessonCodes },
      }));
    }

    // Standard-bundle codes: released via the groups model, grouped by bundle.
    const byBundle = new Map();
    items
      .filter((c) => c.bundle_type === "standard" && c.assignment_id)
      .forEach((c) => {
        if (!byBundle.has(c.assignment_id)) byBundle.set(c.assignment_id, []);
        byBundle.get(c.assignment_id).push(c.group_code);
      });
    byBundle.forEach((codes, assignmentId) => {
      tasks.push(request(`groups/classrooms/${classroomId}/assignments/${assignmentId}`, {
        method: "PUT",
        payload: { assignments: codes.map((code) => ({ group_code: code, track_type: null, standard_code: null })) },
      }));
    });

    await Promise.all(tasks);
  }, [classroomId]);

  return { clearableCodes, clearCodes };
};
